import { Translation } from '@shared/types/translation';

export const ADMIN_LABELS = {
  ADMIN_ID: {
    english: 'Admin ID',
    polish: 'Identyfikator administratora',
  },
} as const satisfies Record<string, Translation>;

// tAdmin(key) derives its lookup from this object (english -> polish), so
// keys here must match the exact English label strings used in
// ADMIN_MODULES. No translation yet, intentionally omitted (tAdmin() falls
// back to the raw key, so this is a no-op): "Service Request Management"
// (title), "View Service Request List", "View Service Request Details",
// "Update Service Request Status", "List Invoice", "View Invoice".
export const ADMIN_PRIVILEGE_LABELS = {
  SELECT_ALL: {
    english: 'Select All',
    polish: 'Zaznacz wszystko',
  },

  ADMIN_USER_MANAGEMENT: {
    english: 'Admin User Management',
    polish: 'Zarządzanie użytkownikami administracyjnymi',
  },
  ADMIN_USER_LIST: {
    english: 'List Admin Users',
    polish: 'Lista użytkowników administracyjnych',
  },
  ADMIN_USER_CREATE: {
    english: 'Add Admin User',
    polish: 'Dodaj użytkownika administracyjnego',
  },
  ADMIN_USER_EDIT: {
    english: 'Edit Admin User',
    polish: 'Edytuj użytkownika administracyjnego',
  },
  ADMIN_USER_VIEW: {
    english: 'View Admin User Details',
    polish: 'Wyświetl szczegóły użytkownika administracyjnego',
  },
  ADMIN_USER_ACTIVATE_DEACTIVATE: {
    english: 'Activate / Deactivate Admin User',
    polish: 'Aktywuj / Dezaktywuj użytkownika administracyjnego',
  },

  SUBSCRIPTION_MANAGEMENT: {
    english: 'Subscription Management',
    polish: 'Zarządzanie subskrypcjami',
  },
  SUBSCRIPTION_LIST: {
    english: 'List Subscriptions',
    polish: 'Lista subskrypcji',
  },
  SUBSCRIPTION_CREATE: {
    english: 'Create Subscription',
    polish: 'Utwórz subskrypcję',
  },
  SUBSCRIPTION_EDIT: {
    english: 'Edit Subscription',
    polish: 'Edytuj subskrypcję',
  },
  SUBSCRIPTION_VIEW: {
    english: 'View Subscription Details',
    polish: 'Wyświetl szczegóły subskrypcji',
  },
  SUBSCRIPTION_ACTIVATE_DEACTIVATE: {
    english: 'Activate / Deactivate Subscription',
    polish: 'Aktywuj / Dezaktywuj subskrypcję',
  },

  OFFER_MANAGEMENT: {
    english: 'Offer Management',
    polish: 'Zarządzanie ofertami',
  },
  OFFER_LIST: {
    english: 'List Offers',
    polish: 'Lista ofert',
  },
  OFFER_CREATE: {
    english: 'Create Offer',
    polish: 'Utwórz ofertę',
  },
  OFFER_EDIT: {
    english: 'Edit Offer',
    polish: 'Edytuj ofertę',
  },
  OFFER_VIEW: {
    english: 'View Offer Details',
    polish: 'Wyświetl szczegóły oferty',
  },
  OFFER_ACTIVATE_DEACTIVATE: {
    english: 'Activate / Deactivate Offer',
    polish: 'Aktywuj / Dezaktywuj ofertę',
  },

  CLIENT_MANAGEMENT: {
    english: 'Client Management',
    polish: 'Zarządzanie klientami',
  },
  CLIENT_LIST: {
    english: 'List Clients',
    polish: 'Lista klientów',
  },
  CLIENT_CREATE: {
    english: 'Create Client',
    polish: 'Utwórz klienta',
  },
  CLIENT_EDIT: {
    english: 'Edit Client',
    polish: 'Edytuj klienta',
  },
  CLIENT_VIEW: {
    english: 'View Client Details',
    polish: 'Wyświetl szczegóły klienta',
  },
  CLIENT_ACTIVATE_DEACTIVATE: {
    english: 'Activate / Deactivate Client',
    polish: 'Aktywuj / Dezaktywuj klienta',
  },

  FACILITY_ACTIVATE_DEACTIVATE: {
    english: 'Activate / Deactivate Facility',
    polish: 'Aktywuj / Dezaktywuj placówkę',
  },

  TECHNICIAN_MANAGEMENT: {
    english: 'Technician Management',
    polish: 'Zarządzanie technikami',
  },
  TECHNICIAN_LIST: {
    english: 'List Technicians',
    polish: 'Lista techników',
  },
  TECHNICIAN_CREATE: {
    english: 'Create Technician',
    polish: 'Dodaj technika',
  },
  TECHNICIAN_EDIT: {
    english: 'Edit Technician',
    polish: 'Edytuj technika',
  },
  TECHNICIAN_VIEW: {
    english: 'View Technician Details',
    polish: 'Wyświetl szczegóły technika',
  },
  TECHNICIAN_ACTIVATE_DEACTIVATE: {
    english: 'Activate / Deactivate Technician',
    polish: 'Aktywuj / Dezaktywuj technika',
  },

  FORM_TEMPLATE_MANAGEMENT: {
    english: 'Task Form Template Management',
    polish: 'Zarządzanie szablonami formularzy zadań',
  },
  FORM_TEMPLATE_LIST: {
    english: 'List of Task Form Templates',
    polish: 'Lista szablonów formularzy zadań',
  },
  FORM_TEMPLATE_CREATE: {
    english: 'Add Task Form Template',
    polish: 'Dodaj szablon formularza zadania',
  },
  FORM_TEMPLATE_EDIT: {
    english: 'Edit Task Form Template',
    polish: 'Edytuj szablon formularza zadania',
  },
  FORM_TEMPLATE_VIEW: {
    english: 'View Task Form Template',
    polish: 'Wyświetl szablon formularza zadania',
  },

  CERTIFICATE_TEMPLATE_MANAGEMENT: {
    english: 'Certificate Template Management',
    polish: 'Zarządzanie szablonami certyfikatów',
  },
  CERTIFICATE_TEMPLATE_LIST: {
    english: 'List of Certificate Templates',
    polish: 'Lista szablonów certyfikatów',
  },
  CERTIFICATE_TEMPLATE_CREATE: {
    english: 'Add Certificate Template',
    polish: 'Dodaj szablon certyfikatu',
  },
  CERTIFICATE_TEMPLATE_EDIT: {
    english: 'Edit Certificate Template',
    polish: 'Edytuj szablon certyfikatu',
  },
  CERTIFICATE_TEMPLATE_VIEW: {
    english: 'View Certificate Template Details',
    polish: 'Wyświetl szczegóły szablonu certyfikatu',
  },

  CERTIFICATE_MANAGEMENT: {
    english: 'Certificate Management',
    polish: 'Zarządzanie certyfikatami',
  },
  CERTIFICATE_GENERATED_LIST: {
    english: 'List of Certificate Generated',
    polish: 'Lista wygenerowanych certyfikatów',
  },
  CERTIFICATE_GENERATED_VIEW: {
    english: 'View Certificate Details',
    polish: 'Wyświetl szczegóły certyfikatu',
  },
  CERTIFICATE_VIEW_DOWNLOAD: {
    english: 'View & Download Certificate',
    polish: 'Wyświetl i pobierz certyfikat',
  },

  SERVICE_TYPE_MANAGEMENT: {
    english: 'Service Type Management',
    polish: 'Zarządzanie typami usług',
  },
  SERVICE_TYPE_LIST: {
    english: 'List of Service Types',
    polish: 'Lista typów usług',
  },
  SERVICE_TYPE_CREATE: {
    english: 'Create Service Type',
    polish: 'Utwórz typ usługi',
  },
  SERVICE_TYPE_EDIT: {
    english: 'Edit Service Type',
    polish: 'Edytuj typ usługi',
  },
  SERVICE_TYPE_VIEW: {
    english: 'View Service Type',
    polish: 'Wyświetl typ usługi',
  },
  SERVICE_TYPE_ACTIVATE_DEACTIVATE: {
    english: 'Activate / Deactivate Service Type',
    polish: 'Aktywuj / Dezaktywuj typ usługi',
  },

  SERVICE_REQUEST_ASSIGN: {
    english: 'Assign / Schedule Service Requests',
    polish: 'Przypisz / Zaplanuj zgłoszenia serwisowe',
  },
  SERVICE_REQUEST_ACCEPT_REJECT: {
    english: 'Accept / Reject Service Request',
    polish: 'Zaakceptuj / Odrzuć zgłoszenie serwisowe',
  },
  SERVICE_REQUEST_RESCHEDULE: {
    english: 'Reschedule Service Request',
    polish: 'Zmień termin zgłoszeń serwisowych',
  },

  CONTACT_REQUEST_MANAGEMENT: {
    english: 'Contact Request Management',
    polish: 'Zarządzanie zgłoszeniami kontaktowymi',
  },
  CONTACT_REQUEST_LIST: {
    english: 'List of Contact Requests',
    polish: 'Lista zgłoszeń kontaktowych',
  },
  CONTACT_REQUEST_VIEW: {
    english: 'View Specific Contact Request',
    polish: 'Wyświetl konkretne zgłoszenie kontaktowe',
  },
  CONTACT_REQUEST_ASSIGN: {
    english: 'Assign Contact Requests',
    polish: 'Przypisz zgłoszenia kontaktowe',
  },
  CONTACT_REQUEST_UPDATE_STATUS: {
    english: 'Update Contact Request Status',
    polish: 'Zaktualizuj status zgłoszenia kontaktowego',
  },
  CONTACT_REQUEST_ACCEPT_REJECT: {
    english: 'Accept / Reject Contact Request',
    polish: 'Zaakceptuj / Odrzuć zgłoszenie kontaktowe',
  },
  CONTACT_REQUEST_RESCHEDULE: {
    english: 'Reschedule Contact Requests',
    polish: 'Zmień termin zgłoszeń kontaktowych',
  },

  INVOICE_TEMPLATE_MANAGEMENT: {
    english: 'Invoice Template Management',
    polish: 'Zarządzanie szablonami faktur',
  },
  INVOICE_TEMPLATE_LIST: {
    english: 'List of Invoice Templates',
    polish: 'Lista szablonów faktur',
  },
  INVOICE_TEMPLATE_CREATE: {
    english: 'Add Invoice Template',
    polish: 'Dodaj szablon faktury',
  },
  INVOICE_TEMPLATE_EDIT: {
    english: 'Edit Invoice Template',
    polish: 'Edytuj szablon faktury',
  },
  INVOICE_TEMPLATE_VIEW: {
    english: 'View Invoice Template',
    polish: 'Wyświetl szablon faktury',
  },
  INVOICE_TEMPLATE_ACTIVATE_DEACTIVATE: {
    english: 'Activate / Deactivate Invoice Template',
    polish: 'Aktywuj / Dezaktywuj szablon faktury',
  },

  INVOICE_MANAGEMENT: {
    english: 'Invoice Management',
    polish: 'Zarządzanie fakturami',
  },
  INVOICE_DRAFT_LIST: {
    english: 'List of Draft Invoices',
    polish: 'Lista faktur roboczych',
  },
  INVOICE_DRAFT_VIEW: {
    english: 'View Draft Invoice Details',
    polish: 'Wyświetl szczegóły faktury roboczej',
  },
  INVOICE_DRAFT_EDIT: {
    english: 'Edit Draft Invoice Details',
    polish: 'Edytuj szczegóły faktury roboczej',
  },
  INVOICE_PUBLISH: {
    english: 'Update Invoice Status',
    polish: 'Zaktualizuj status faktury',
  },
  INVOICE_DOWNLOAD: {
    english: 'Download Invoice',
    polish: 'Pobierz fakturę',
  },

  QMS_DOC_CATEGORY_MANAGEMENT: {
    english: 'QMS Document Category Management',
    polish: 'Zarządzanie kategoriami dokumentów QMS',
  },
  QMS_DOC_CATEGORY_LIST: {
    english: 'List of Categories',
    polish: 'Lista kategorii',
  },
  QMS_DOC_CATEGORY_CREATE: {
    english: 'Create Category',
    polish: 'Utwórz kategorię',
  },
  QMS_DOC_CATEGORY_EDIT: {
    english: 'Edit Category',
    polish: 'Edytuj kategorię',
  },
  QMS_DOC_CATEGORY_VIEW: {
    english: 'View Category Details',
    polish: 'Wyświetl szczegóły kategorii',
  },
  QMS_DOC_CATEGORY_ACTIVATE_DEACTIVATE: {
    english: 'Activate / Deactivate Category',
    polish: 'Aktywuj / Dezaktywuj kategorię',
  },

  QMS_DOC_TEMPLATE_MANAGEMENT: {
    english: 'QMS Document Template Management',
    polish: 'Zarządzanie szablonami dokumentów QMS',
  },
  QMS_DOC_TEMPLATE_LIST: {
    english: 'List of Templates',
    polish: 'Lista szablonów',
  },
  QMS_DOC_TEMPLATE_CREATE: {
    english: 'Create Template',
    polish: 'Utwórz szablon',
  },
  QMS_DOC_TEMPLATE_EDIT: {
    english: 'Edit Template',
    polish: 'Edytuj szablon',
  },
  QMS_DOC_TEMPLATE_VIEW: {
    english: 'View Template Details',
    polish: 'Wyświetl szczegóły szablonu',
  },
  QMS_DOC_TEMPLATE_ACTIVATE_DEACTIVATE: {
    english: 'Activate / Deactivate Template',
    polish: 'Aktywuj / Dezaktywuj szablon',
  },

  // Sheet's row says "...Update Requests Management"; code's title omits
  // "Management" — same concept, applied to the code's actual key text.
  QMS_UPDATE_REQUESTS: {
    english: 'QMS Document Update Requests',
    polish: 'Zarządzanie zgłoszeniami aktualizacji dokumentów QMS',
  },
  QMS_UPDATE_REQUEST_LIST: {
    english: 'List of Client Requests',
    polish: 'Lista zgłoszeń klientów',
  },
  QMS_UPDATE_REQUEST_EDIT: {
    english: 'Edit Request',
    polish: 'Edytuj zgłoszenie',
  },
  QMS_UPDATE_REQUEST_VIEW: {
    english: 'View Client Request Details',
    polish: 'Wyświetl szczegóły zgłoszenia klienta',
  },
  QMS_UPDATE_REQUEST_UPDATE_STATUS: {
    english: 'Update Document Status',
    polish: 'Zaktualizuj status dokumentu',
  },

  OTHER_MODULES: {
    english: 'Other Modules',
    polish: 'Pozostałe moduły',
  },
  DASHBOARD: {
    english: 'Dashboard',
    polish: 'Pulpit',
  },
  EXPORT_REPORTS: {
    english: 'Export Reports',
    polish: 'Eksport raportów',
  },
} as const satisfies Record<string, Translation>;
